import { Inject, Injectable } from '@angular/core';
import { CrmDependencies, CrmInjectionToken } from '@galaxy/crm/static';
import { Salesperson, SalespersonService, SalesSdk } from '@vendasta/sales';
import { PagedResponse } from '@vendasta/sales/lib/salesperson/salesperson.service';
import {
  catchError,
  combineLatest,
  expand,
  map,
  Observable,
  of,
  reduce,
  ReplaySubject,
  switchMap,
  take,
  takeWhile,
  withLatestFrom,
} from 'rxjs';

export interface SalespersonWithDisplayName extends Salesperson {
  displayName: string;
}

interface CachedResult {
  salespersonId: string;
  salesperson: SalespersonWithDisplayName;
  index: number;
}

export const salespersonFilterFn = (salesperson: SalespersonWithDisplayName, searchTerm: string): boolean => {
  const includesFullName = salesperson.displayName?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesFirstName = salesperson.firstName?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesLastName = salesperson.lastName?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesSalespersonId = salesperson.salespersonId?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesUserId = salesperson.userId?.toLowerCase().includes(searchTerm?.toLowerCase());
  const includesEmail = salesperson.email?.toLowerCase().includes(searchTerm?.toLowerCase());
  return (
    includesFullName ||
    includesFirstName ||
    includesLastName ||
    includesUserId ||
    includesSalespersonId ||
    includesEmail
  );
};

const convertToReadableSalespeople = (salespeople: Salesperson[]): SalespersonWithDisplayName[] =>
  salespeople.map(
    (salesperson) =>
      ({
        ...salesperson,
        displayName: `${salesperson?.firstName ?? ''} ${salesperson?.lastName ?? ''}`.trim(),
      }) as SalespersonWithDisplayName,
  );

@Injectable({ providedIn: 'root' })
export class PartnerCRMSalespersonService {
  allSalespeople$$: ReplaySubject<SalespersonWithDisplayName[]> = new ReplaySubject(1);

  constructor(
    @Inject(CrmInjectionToken) private readonly crmConfig: CrmDependencies,
    @Inject(SalespersonService) private readonly salespersonService: SalesSdk,
  ) {
    this.crmConfig.namespace$
      .pipe(
        take(1),
        switchMap((namespace) => this.fetchAllSalespeople(namespace)),
      )
      .subscribe((salespeople) => {
        this.allSalespeople$$.next(salespeople);
      });
  }

  private fetchSalespeople(
    namespace: string,
    cursor: string,
    pageSize: number,
  ): Observable<PagedResponse<Salesperson>> {
    return this.salespersonService.listSalespersons({ partnerId: namespace, marketId: '' }, cursor, pageSize);
  }

  private fetchAllSalespeople(namespace: string): Observable<SalespersonWithDisplayName[]> {
    return this.fetchSalespeople(namespace, '', 500).pipe(
      expand((response) => this.fetchSalespeople(namespace, response.nextCursor, 500)),
      takeWhile((response) => !!response.nextCursor, true),
      reduce((all, { results }) => all.concat(results), []),
      map((salespeople) => convertToReadableSalespeople(salespeople)),
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
    );
  }

  getMultiSalesperson(salespersonIds: string[]): Observable<SalespersonWithDisplayName[]> {
    const cachedResults$ = this.allSalespeople$$.pipe(
      map((salespeople) => {
        const mappedToCachedSalespeople = salespersonIds.map(
          (id, index) =>
            ({
              salespersonId: id,
              salesperson: salespeople.find((sp) => sp.salespersonId === id) ?? null,
              index,
            }) as CachedResult,
        );
        return mappedToCachedSalespeople;
      }),
    );
    const notFound$ = cachedResults$.pipe(
      map((mappedToCachedSalespeople) => mappedToCachedSalespeople.filter((m) => m.salesperson === null)),
    );
    const notFoundIds$ = notFound$.pipe(map((notFound) => notFound.map((m) => m.salespersonId as string)));
    const notFoundMappings$ = notFound$.pipe(
      map((notFound) => {
        const notFoundMap = new Map<string, number>();
        notFound.forEach((m) => notFoundMap.set(m.salespersonId, m.index));
        return notFoundMap;
      }),
    );

    return combineLatest([this.crmConfig.namespace$, notFoundIds$]).pipe(
      switchMap(([namespace, idsToGet]) => {
        if (idsToGet.length === 0) return of([]);
        return this.salespersonService.getMultiSalesperson(namespace, idsToGet);
      }),
      map((salespeople) => convertToReadableSalespeople(salespeople)),
      withLatestFrom(cachedResults$, notFoundMappings$),
      map(([getMultiResults, cachedResults, notFoundMappings]) => {
        for (const notFound of cachedResults.filter((m) => m.salesperson === null)) {
          const index = notFoundMappings.get(notFound.salespersonId);
          cachedResults[index].salesperson =
            getMultiResults.find((m) => m.salespersonId === notFound.salespersonId) || null;
        }
        return cachedResults.map((m) => m.salesperson);
      }),
      catchError((error) => {
        console.error(error);
        return cachedResults$.pipe(map((cachedResults) => cachedResults.map((m) => m.salesperson)));
      }),
    );
  }
}
